import { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate, useMatch } from 'react-router-dom';
import { SignInButton, UserButton, SignedIn, SignedOut, useAuth, useUser } from '@clerk/clerk-react';
import { api } from './utils/api';
import { useMeRole, refreshMeRole } from './hooks/useMeRole';
import { useAdmin } from './hooks/useAdmin';
import { CartProvider, useCart } from './contexts/CartContext';
import CartPanel from './components/CartPanel';
import BottomNav from './components/BottomNav';
import InstallPWA from './components/InstallPWA';
import NotificationBell from './components/NotificationBell';
import Profile from './pages/Profile';
import JoinMarket from './pages/JoinMarket';
import MyItems from './pages/MyItems';
import MyPurchases from './pages/MyPurchases';
import Marketplace from './pages/Marketplace';
import MarketDetail from './pages/MarketDetail';
import VendorPublic from './pages/VendorPublic';
import ItemDetail from './pages/ItemDetail';
import Review from './pages/Review';
import CheckoutSuccess from './pages/CheckoutSuccess';
import CheckoutCancel from './pages/CheckoutCancel';
import Admin from './pages/Admin';
import Search from './pages/Search';
import VendorLogin from './pages/VendorLogin';
import Account from './pages/Account';
import OwnerDashboard from './pages/OwnerDashboard';
import VendorReports from './pages/VendorReports';
import Pickup from './pages/Pickup';
import SignInRedirect from './pages/SignInRedirect';
import InviteRedeem from './pages/InviteRedeem';
import './App.css';

/**
 * When signed in: only redirect to vendor flow when user hits a vendor-only route without FleaMarketId.
 * Customer routes (home, search, my-purchases, etc.) are never redirected. User type from DB only.
 */
function OnboardingGuard({ children }) {
  const { getToken } = useAuth();
  const location = useLocation();
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await api('/api/vendors/me', { getToken });
        const data = await res.json().catch(() => ({}));
        if (!cancelled && res.ok) setVendor(data);
        else if (!cancelled) setVendor(null);
      } catch {
        if (!cancelled) setVendor(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [getToken, location.pathname]);

  const path = location.pathname;
  // Owner: no vendor/me or onboarding required — just show dashboard. OwnerDashboard handles role + reports.
  if (path === '/owner' || path.startsWith('/owner/')) return children;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-slate-500 dark:text-slate-400">
        Loading…
      </div>
    );
  }

  const hasMarket = vendor?.fleaMarketId != null;
  const hasStoreName = (vendor?.storeName ?? '').trim() !== '';
  const hasStripeAccount = vendor?.stripeAccountId != null && vendor.stripeAccountId.trim() !== '';

  const publicRoutes = ['/', '/market', '/vendor', '/item', '/review', '/search', '/signup', '/vendor/login', '/pickup', '/invite'];
  const isPublicRoute = publicRoutes.some(route => path === route || path.startsWith('/market/') || path.startsWith('/vendor/') || path.startsWith('/item/') || path.startsWith('/review/') || path.startsWith('/search') || path.startsWith('/signup/') || path.startsWith('/pickup') || path.startsWith('/invite/')) && path !== '/vendor/reports';
  if (isPublicRoute) return children;
  if (path === '/account') return children;
  if (path === '/my-purchases') return children;

  // Vendor-only routes: require FleaMarketId or redirect to vendor login
  const vendorOnlyRoutes = ['/profile', '/join-market', '/my-items', '/items', '/owner', '/vendor/reports'];
  const isVendorOnlyRoute = vendorOnlyRoutes.some(route => path === route || path.startsWith(route + '/'));
  if (!hasMarket && isVendorOnlyRoute) {
    return <Navigate to="/vendor/login" replace />;
  }

  if (hasMarket && !hasStoreName && path !== '/profile') {
    return <Navigate to="/profile" replace />;
  }
  const stripeProtectedRoutes = ['/my-items', '/items/create', '/items/new'];
  const isStripeProtectedRoute = stripeProtectedRoutes.some(route => path.startsWith(route));
  if (hasMarket && hasStoreName && isStripeProtectedRoute && !hasStripeAccount && path !== '/profile') {
    return <Navigate to="/profile?stripe=required" replace />;
  }

  return children;
}

/** Redirect booth vendors away from customer browse/shop routes. */
function VendorBrowseGuard({ children }) {
  const location = useLocation();
  const { isVendor, isOwner, loading } = useMeRole();
  const path = location.pathname;

  if (loading) return children;

  const isBoothVendor = isVendor && !isOwner;
  if (!isBoothVendor) return children;

  const isCustomerBrowse =
    path === '/' ||
    path === '/search' ||
    path.startsWith('/search') ||
    path.startsWith('/market/') ||
    path.startsWith('/item/') ||
    path === '/account' ||
    path === '/my-purchases' ||
    /^\/vendor\/\d+/.test(path);

  if (isCustomerBrowse) {
    return <Navigate to="/my-items" replace />;
  }
  return children;
}

/** Redirect owners to dashboard when they try to access any other route. Owners only see the dashboard. */
function OwnerGuard({ children }) {
  const location = useLocation();
  const { isOwner, loading } = useMeRole();
  const path = location.pathname;
  const allowed =
    path === '/owner' ||
    path.startsWith('/owner/') ||
    path === '/admin' ||
    path === '/admin/reports' ||
    path.startsWith('/signup/') ||
    path === '/vendor/login' ||
    path.startsWith('/invite/');
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-slate-500 dark:text-slate-400">
        Loading…
      </div>
    );
  }
  if (isOwner && !allowed) {
    return <Navigate to="/owner" replace />;
  }
  return children;
}

/** Redirect admins to admin dashboard when they try to access any other route. Admins only see /admin. */
function AdminGuard({ children }) {
  const location = useLocation();
  const { isAdmin, loading } = useAdmin();
  const path = location.pathname;
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-slate-500 dark:text-slate-400">
        Loading…
      </div>
    );
  }
  const adminPaths = ['/admin', '/admin/reports', '/owner'];
  const adminAllowed = adminPaths.includes(path) || path.startsWith('/owner/');
  if (isAdmin && !adminAllowed) {
    return <Navigate to="/admin" replace />;
  }
  return children;
}

const NO_HEADER_PATHS = ['/join-market', '/checkout/success', '/checkout/cancel'];

function AmazonHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isOwner, isVendor, loading: roleLoading } = useMeRole();
  const { isAdmin, loading: adminLoading } = useAdmin();
  const { getTotal } = useCart();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const itemCount = getTotal();

  if (NO_HEADER_PATHS.includes(location.pathname)) return null;
  if ((!roleLoading && isOwner) || (!adminLoading && isAdmin)) return null;
  if (location.pathname.startsWith('/owner/')) return null;

  const handleCartClick = () => {
    window.dispatchEvent(new CustomEvent('openCart'));
  };

  const handleMenuClick = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleMenuClose = () => {
    setIsMenuOpen(false);
  };

  const handleNavClick = (path) => {
    navigate(path);
    setIsMenuOpen(false);
  };

  return (
    <>
      <div className="bg-[#232F3E] text-white w-full fixed top-0 left-0 right-0 z-40">
        <div className="max-w-screen-md mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button 
              onClick={handleMenuClick}
              className="text-white" 
              aria-label="Menu"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <div className="flex items-center">
              <span className="text-xl font-semibold text-white">FleaMarket</span>
              <span className="text-xl font-semibold text-orange-400 ml-1">Hub</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <SignedOut>
              <SignInButton mode="modal" forceRedirectUrl={window.location.href}>
                <button
                  type="button"
                  className="text-sm font-medium px-3 py-1.5 rounded-lg border border-white/30 hover:border-orange-400 hover:text-orange-400 transition-colors"
                >
                  Sign in
                </button>
              </SignInButton>
            </SignedOut>
            <SignedIn>
              <div className="flex items-center gap-2">
                {isVendor && !isOwner && <NotificationBell isVendor={isVendor} />}
                <UserButton 
                  afterSignOutUrl="/"
                  appearance={{
                    elements: {
                      avatarBox: 'h-8 w-8',
                    },
                  }}
                />
              </div>
            </SignedIn>
            {!isVendor && (
            <button 
              onClick={handleCartClick}
              className="relative cursor-pointer"
              aria-label={`Shopping cart with ${itemCount} items`}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              {itemCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-orange-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  {itemCount > 9 ? '9+' : itemCount}
                </span>
              )}
            </button>
            )}
          </div>
        </div>
      </div>

      {/* Hamburger Menu Drawer */}
      {isMenuOpen && (
        <>
          <div 
            className="fixed inset-0 bg-black/50 z-[45] transition-opacity"
            onClick={handleMenuClose}
            aria-hidden="true"
          />
          <div className={`fixed top-0 left-0 h-full w-64 bg-[#232F3E] text-white z-50 transform transition-transform duration-300 ease-out shadow-2xl ${
            isMenuOpen ? 'translate-x-0' : '-translate-x-full'
          }`}>
            <div className="p-4 border-b border-white/20">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <span className="text-xl font-semibold text-white">FleaMarket</span>
                  <span className="text-xl font-semibold text-orange-400 ml-1">Hub</span>
                </div>
                <button 
                  onClick={handleMenuClose}
                  className="text-white hover:text-orange-400 transition-colors"
                  aria-label="Close menu"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <nav className="flex flex-col p-4">
              {isVendor ? (
                <>
                  <button
                    onClick={() => handleNavClick('/my-items')}
                    className="text-left py-3 px-4 rounded-lg hover:bg-white/10 transition-colors flex items-center gap-3"
                  >
                    <span className="text-xl">📦</span>
                    <span>My Items</span>
                  </button>
                  <button
                    onClick={() => handleNavClick('/vendor/reports')}
                    className="text-left py-3 px-4 rounded-lg hover:bg-white/10 transition-colors flex items-center gap-3"
                  >
                    <span className="text-xl">📈</span>
                    <span>Reports</span>
                  </button>
                  <button
                    onClick={() => handleNavClick('/profile')}
                    className="text-left py-3 px-4 rounded-lg hover:bg-white/10 transition-colors flex items-center gap-3"
                  >
                    <span className="text-xl">👤</span>
                    <span>Profile</span>
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => handleNavClick('/')}
                    className="text-left py-3 px-4 rounded-lg hover:bg-white/10 transition-colors flex items-center gap-3"
                  >
                    <span className="text-xl">🏠</span>
                    <span>Home</span>
                  </button>
                  <button
                    onClick={() => handleNavClick('/search')}
                    className="text-left py-3 px-4 rounded-lg hover:bg-white/10 transition-colors flex items-center gap-3"
                  >
                    <span className="text-xl">🔍</span>
                    <span>Search</span>
                  </button>
                  <SignedIn>
                    <button
                      onClick={() => handleNavClick('/my-purchases')}
                      className="text-left py-3 px-4 rounded-lg hover:bg-white/10 transition-colors flex items-center gap-3"
                    >
                      <span className="text-xl">🛍️</span>
                      <span>My Purchases</span>
                    </button>
                    <button
                      onClick={() => handleNavClick('/account')}
                      className="text-left py-3 px-4 rounded-lg hover:bg-white/10 transition-colors flex items-center gap-3"
                    >
                      <span className="text-xl">👤</span>
                      <span>Account</span>
                    </button>
                  </SignedIn>
                  <SignedOut>
                    <SignInButton mode="modal" forceRedirectUrl={window.location.href}>
                      <button
                        type="button"
                        className="text-left py-3 px-4 rounded-lg hover:bg-white/10 transition-colors flex items-center gap-3 w-full"
                      >
                        <span className="text-xl">🔑</span>
                        <span>Sign in</span>
                      </button>
                    </SignInButton>
                  </SignedOut>
                </>
              )}
            </nav>
          </div>
        </>
      )}
    </>
  );
}

function SearchBar() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="w-full">
      <div className="relative">
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search items…"
          className="w-full h-12 text-base px-4 pr-14 rounded-full border-0 bg-gray-100 text-slate-900 focus:outline-none focus:ring-2 focus:ring-orange-400"
        />
        <button
          type="submit"
          className="absolute right-1 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-orange-500 hover:bg-orange-600 active:bg-orange-700 transition-colors flex items-center justify-center"
          aria-label="Search"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 text-white"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </button>
      </div>
    </form>
  );
}

/** On item pages: replace search with a clear path to the vendor storefront. */
function VisitVendorStoreBar() {
  const navigate = useNavigate();
  const match = useMatch('/item/:id');
  const id = match?.params?.id;
  const [vendor, setVendor] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!id) return undefined;
    setVendor(null);
    (async () => {
      try {
        const res = await api(`/api/items/${id}`, {});
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && data?.vendorId) {
          setVendor({
            id: data.vendorId,
            name: data.vendorName || 'Vendor store',
          });
        }
      } catch {
        // keep bar empty on failure
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (!id) return null;

  if (!vendor?.id) {
    return (
      <div className="w-full h-12 rounded-full bg-gray-100 dark:bg-slate-800 animate-pulse" aria-hidden />
    );
  }

  return (
    <button
      type="button"
      onClick={() => navigate(`/vendor/${vendor.id}`)}
      className="w-full h-12 px-4 rounded-full bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white font-semibold text-base flex items-center justify-between gap-2 transition-colors"
    >
      <span className="truncate">Visit vendor store</span>
      <span className="shrink-0 text-sm font-medium opacity-90 truncate max-w-[45%]">
        {vendor.name} →
      </span>
    </button>
  );
}

function App() {
  const [isCartOpen, setIsCartOpen] = useState(false);
  const location = useLocation();
  const { isOwner, isVendor, loading: roleLoading } = useMeRole();
  const { isAdmin, loading: adminLoading } = useAdmin();
  const { getToken, isSignedIn } = useAuth();
  const { user } = useUser();

  // Demo: bind lrjoseph → owner/admin; scrumguidance → booth vendor
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!isSignedIn || !user) return;
      const email = user.primaryEmailAddress?.emailAddress;
      if (!email) return;
      const lower = email.toLowerCase();

      if (lower === 'lrjoseph0728@gmail.com') {
        if (sessionStorage.getItem('demo-bind-done') === email) return;
        try {
          const res = await api('/api/admin/demo-bind', {
            getToken,
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email }),
          });
          if (!cancelled && res.ok) {
            sessionStorage.setItem('demo-bind-done', email);
            refreshMeRole();
            window.dispatchEvent(new CustomEvent('admin-role-changed'));
            if (window.location.pathname === '/' || window.location.pathname === '') {
              window.location.replace('/admin');
            } else {
              window.location.reload();
            }
          }
        } catch {
          // ignore
        }
        return;
      }

      if (lower === 'scrumguidance@gmail.com') {
        if (sessionStorage.getItem('demo-bind-vendor-done') === email) return;
        try {
          const res = await api('/api/admin/demo-bind-vendor', {
            getToken,
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email }),
          });
          if (!cancelled && res.ok) {
            sessionStorage.setItem('demo-bind-vendor-done', email);
            refreshMeRole();
            if (
              window.location.pathname === '/' ||
              window.location.pathname === '' ||
              window.location.pathname === '/admin' ||
              window.location.pathname.startsWith('/owner')
            ) {
              window.location.replace('/my-items');
            } else {
              window.location.reload();
            }
          }
        } catch {
          // ignore
        }
      }
    })();
    return () => { cancelled = true; };
  }, [isSignedIn, user, getToken]);

  const handleCloseCart = () => {
    setIsCartOpen(false);
  };

  // Close cart when navigating (e.g. tapping Home, Search, Items, Profile in bottom nav)
  useEffect(() => {
    if (isCartOpen) setIsCartOpen(false);
  }, [location.pathname]); // Only close on pathname change, not when isCartOpen changes

  // Listen for custom cart open event from bottom nav
  useEffect(() => {
    const handleOpenCart = () => {
      setIsCartOpen(true);
    };
    window.addEventListener('openCart', handleOpenCart);
    return () => window.removeEventListener('openCart', handleOpenCart);
  }, []);

  const isVendorLoginPage = location.pathname === '/vendor/login' || location.pathname.startsWith('/signup/') || location.pathname.startsWith('/invite/');
  const isAccountPage = location.pathname === '/account';

  return (
    <CartProvider>
      <AppContent 
        isCartOpen={isCartOpen}
        handleCloseCart={handleCloseCart}
        isVendorLoginPage={isVendorLoginPage}
        isAccountPage={isAccountPage}
        isOwner={isOwner}
        isVendor={isVendor}
        roleLoading={roleLoading}
        isAdmin={isAdmin}
        adminLoading={adminLoading}
      />
    </CartProvider>
  );
}

function AppContent({ 
  isCartOpen, 
  handleCloseCart, 
  isVendorLoginPage, 
  isAccountPage,
  isOwner,
  isVendor,
  roleLoading,
  isAdmin,
  adminLoading
}) {
  const location = useLocation();
  const noHeaderPaths = ['/join-market', '/checkout/success', '/checkout/cancel', '/owner', '/admin'];
  const addTopPadding = !noHeaderPaths.includes(location.pathname) && !location.pathname.startsWith('/admin/') && !location.pathname.startsWith('/owner/');

  const isItemPage = Boolean(location.pathname.match(/^\/item\/[^/]+$/));

  const showSearchBar =
    !roleLoading &&
    !isOwner &&
    !isVendor &&
    !isAdmin &&
    !adminLoading &&
    !isItemPage &&
    location.pathname !== '/my-items' &&
    location.pathname !== '/profile' &&
    location.pathname !== '/my-purchases' &&
    location.pathname !== '/account' &&
    location.pathname !== '/vendor/login' &&
    !location.pathname.match(/^\/vendor\/\d+$/) &&
    location.pathname !== '/join-market' &&
    location.pathname !== '/checkout/success' &&
    location.pathname !== '/checkout/cancel' &&
    !location.pathname.startsWith('/signup/') &&
    !location.pathname.startsWith('/invite/');

  const showVisitVendorBar =
    isItemPage &&
    !roleLoading &&
    !isOwner &&
    !isVendor &&
    !isAdmin &&
    !adminLoading;

  return (
    <>
      <AmazonHeader />
      <div className={`max-w-screen-md mx-auto px-4 md:px-8 pb-bottom-nav overflow-x-hidden ${addTopPadding ? 'pt-28 md:pt-36' : 'pt-4 md:pt-8'}`}>
        {addTopPadding && (
        <header className="flex flex-col gap-4 mb-8">
          {showSearchBar && <SearchBar />}
          {showVisitVendorBar && <VisitVendorStoreBar />}
          {/* Account bar only on the Account tab */}
          {isAccountPage && (
          <SignedIn>
            <OnboardingGuard>
              <>
                <div className="flex items-center justify-end w-full">
                  <UserButton
                    afterSignOutUrl="/"
                    appearance={{
                      elements: {
                        avatarBox: 'h-14 w-14',
                      },
                    }}
                  />
                </div>
              </>
            </OnboardingGuard>
          </SignedIn>
          )}
        </header>
        )}
        <AdminGuard>
        <OwnerGuard>
        <VendorBrowseGuard>
        <Routes>
          <Route path="/" element={<Marketplace />} />
          <Route path="/search" element={<Search />} />
          <Route path="/market/:id" element={<MarketDetail />} />
          <Route path="/vendor/:id" element={<VendorPublic />} />
          <Route path="/item/:id" element={<ItemDetail />} />
          <Route
            path="/profile"
            element={
              <>
                <SignedIn>
                  <OnboardingGuard>
                    <Profile />
                  </OnboardingGuard>
                </SignedIn>
                <SignedOut>
                  <Navigate to="/" replace />
                </SignedOut>
              </>
            }
          />
          <Route path="/vendor/login" element={<VendorLogin />} />
          <Route path="/invite/:token" element={<InviteRedeem />} />
          <Route path="/signup/:marketCode" element={<VendorLogin />} />
          <Route path="/sign-in" element={<SignInRedirect />} />
          <Route
            path="/join-market"
            element={
              <>
                <SignedIn>
                  <OnboardingGuard>
                    <JoinMarket />
                  </OnboardingGuard>
                </SignedIn>
                <SignedOut>
                  <Navigate to="/" replace />
                </SignedOut>
              </>
            }
          />
          <Route path="/checkout/success" element={<CheckoutSuccess />} />
          <Route path="/checkout/cancel" element={<CheckoutCancel />} />
          <Route path="/pickup" element={<Pickup />} />
          <Route
            path="/review/:id"
            element={
              <>
                <SignedIn>
                  <Review />
                </SignedIn>
                <SignedOut>
                  <Navigate to="/" replace />
                </SignedOut>
              </>
            }
          />
          <Route
            path="/my-items"
            element={
              <>
                <SignedIn>
                  <OnboardingGuard>
                    <MyItems />
                  </OnboardingGuard>
                </SignedIn>
                <SignedOut>
                  <Navigate to="/" replace />
                </SignedOut>
              </>
            }
          />
          <Route
            path="/my-purchases"
            element={
              <>
                <SignedIn>
                  <MyPurchases />
                </SignedIn>
                <SignedOut>
                  <Navigate to="/" replace />
                </SignedOut>
              </>
            }
          />
          <Route
            path="/account"
            element={
              <>
                <SignedIn>
                  <Account />
                </SignedIn>
                <SignedOut>
                  <Navigate to="/" replace />
                </SignedOut>
              </>
            }
          />
          <Route
            path="/owner"
            element={
              <>
                <SignedIn>
                  <OnboardingGuard>
                    <OwnerDashboard />
                  </OnboardingGuard>
                </SignedIn>
                <SignedOut>
                  <Navigate to="/" replace />
                </SignedOut>
              </>
            }
          />
          <Route
            path="/owner/sales"
            element={
              <>
                <SignedIn>
                  <OnboardingGuard>
                    <OwnerDashboard />
                  </OnboardingGuard>
                </SignedIn>
                <SignedOut>
                  <Navigate to="/" replace />
                </SignedOut>
              </>
            }
          />
          <Route
            path="/owner/projected"
            element={
              <>
                <SignedIn>
                  <OnboardingGuard>
                    <OwnerDashboard />
                  </OnboardingGuard>
                </SignedIn>
                <SignedOut>
                  <Navigate to="/" replace />
                </SignedOut>
              </>
            }
          />
          <Route
            path="/vendor/reports"
            element={
              <>
                <SignedIn>
                  <OnboardingGuard>
                    <VendorReports />
                  </OnboardingGuard>
                </SignedIn>
                <SignedOut>
                  <Navigate to="/" replace />
                </SignedOut>
              </>
            }
          />
          <Route
            path="/admin"
            element={
              <>
                <SignedIn>
                  <Admin />
                </SignedIn>
                <SignedOut>
                  <Navigate to="/" replace />
                </SignedOut>
              </>
            }
          />
          <Route
            path="/admin/reports"
            element={
              <>
                <SignedIn>
                  <Admin />
                </SignedIn>
                <SignedOut>
                  <Navigate to="/" replace />
                </SignedOut>
              </>
            }
          />
        </Routes>
        </VendorBrowseGuard>
        </OwnerGuard>
        </AdminGuard>
        <CartPanel isOpen={isCartOpen} onClose={handleCloseCart} />
        <BottomNav onCloseCart={handleCloseCart} isCartOpen={isCartOpen} />
        <InstallPWA />
      </div>
    </>
  );
}

export default App;
